import * as Headless from '@headlessui/react';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import * as Sentry from '@sentry/react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Avatar } from '@/components/ui/catalyst/avatar';
import type { BadgeColor } from '@/components/ui/catalyst/badge';
import { Button } from '@/components/ui/catalyst/button';
import { useTelephonyCti } from '@/contexts/TelephonyCtiContext';
import { useLogoutMutation } from '@/services/api/userService';
import { userLoggedOut } from '@/state/reducers/root';
import { selectUser } from '@/state/selectors/root-selectors';

interface MenuItemConfig {
  label: string;
  path?: string;
  onClick?: () => void;
  badge?: { label: string; color: BadgeColor };
}

interface MenuSectionConfig {
  heading: string;
  items: MenuItemConfig[];
}

const badgeColors: Record<string, string> = {
  lime: 'bg-lime-400/20 text-lime-700 dark:bg-lime-400/10 dark:text-lime-300',
};

export const ProfileSettings: React.FC = () => {
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const [logout] = useLogoutMutation();
  const dispatch = useDispatch();
  const { openPanel: openCommunicationsPanel, providerLabel } = useTelephonyCti();

  const displayName =
    user?.first_name && user?.last_name
      ? `${user.first_name} ${user.last_name}`
      : user?.email || 'Account';

  const initials = user?.first_name
    ? `${user.first_name[0]}${user.last_name ? user.last_name[0] : ''}`
    : user?.email?.slice(0, 1);

  const handleLogout = async () => {
    try {
      await logout().unwrap();
      dispatch(userLoggedOut());
      navigate('/login');
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const menuSections: MenuSectionConfig[] = [
    {
      heading: 'Directories',
      items: [
        { label: 'Carrier Directory', path: '/carrier-directory' },
        { label: 'Global Police Reports', path: '/global-police-reports' },
        { label: 'Police Departments', path: '/policedepartments' },
        { label: 'Provider Directory', path: '/provider-directory' },
      ],
    },
    {
      heading: 'Automation',
      items: [
        { label: 'Bland Command Center', path: '/bland-command-center' },
        { label: 'Bland Templates', path: '/template-editor' },
        { label: 'BPO Command Center', path: '/bpo-command-center' },
        { label: 'Bulk FinchGPT Actions', path: '/bulk-finchgpt-actions' },
        { label: 'Operator Templates', path: '/operator-template-editor' },
      ],
    },
    {
      heading: 'Operations',
      items: [
        { label: 'Contact Queue', path: '/contact-queue', badge: { label: 'Beta', color: 'lime' } },
        { label: 'Day 1', path: '/day-1' },
        { label: 'Matter Updates', path: '/matter-updates' },
        { label: 'PostGrid Mail', path: '/postgrid/dashboard' },
        { label: 'Resources', path: '/resources' },
        { label: 'Risk Overview', path: '/risk-overview' },
        { label: `${providerLabel} CTI`, onClick: openCommunicationsPanel },
        { label: 'The Flock', path: '/flock' },
        { label: 'Voice AI Dashboard', path: '/voice-ai-dashboard' },
      ],
    },
    {
      heading: 'Integrations',
      items: [
        { label: 'CMS Task Import', path: '/profile/cms-import' },
        { label: 'External CMS Reconciliation', path: '/profile/external-cms' },
      ],
    },
    {
      heading: 'Settings',
      items: [{ label: 'General Settings', path: '/settings' }],
    },
  ];

  return (
    <Headless.Menu>
      <Headless.MenuButton
        as={Button}
        plain
        className="flex items-center p-0 dark text-white hover:bg-primary/10 px-0 aspect-square"
      >
        <Avatar
          className="size-8 bg-zinc-900 text-white dark:bg-white dark:text-black"
          src={user?.avatar || undefined}
          initials={initials}
        />
      </Headless.MenuButton>
      <Headless.MenuItems
        transition
        anchor="bottom"
        className="z-[60] w-72 rounded-lg p-0.5 bg-white dark:bg-zinc-800 ring-1 shadow-lg ring-zinc-950/10 dark:ring-white/10 dark:ring-inset outline outline-transparent focus:outline-hidden overflow-y-auto transition data-leave:duration-100 data-leave:ease-in data-closed:data-leave:opacity-0 [--anchor-gap:--spacing(2)] [--anchor-padding:--spacing(1)]"
      >
        {/* Identity header */}
        <div
          className="flex items-center gap-2 px-2 py-1.5 mx-0.5 mt-0.5 cursor-pointer hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-md transition-colors"
          onClick={() => user?.id && navigate(`/users/${user.id}`)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              if (user?.id) navigate(`/users/${user.id}`);
            }
          }}
          role="button"
          tabIndex={0}
        >
          <Avatar
            className="size-7 bg-zinc-900 text-white dark:bg-white dark:text-black shrink-0"
            src={user?.avatar || undefined}
            initials={initials}
          />
          <div className="min-w-0 flex-1">
            <div className="text-xs/4 font-semibold text-zinc-900 dark:text-white truncate">
              {displayName}
            </div>
            <div className="text-[11px]/3.5 font-medium text-zinc-500 truncate">{user?.email}</div>
          </div>
          <ChevronRightIcon className="size-3.5 text-zinc-500 shrink-0" />
        </div>

        {/* Sections */}
        {menuSections.map((section) => (
          <Headless.MenuSection key={section.heading}>
            <Headless.MenuHeading className="mx-0.5 mt-1 mb-0.5 rounded bg-zinc-100 px-2 py-[3px] text-[10px]/4 font-bold uppercase tracking-wider text-zinc-500 dark:bg-zinc-700/50 dark:text-zinc-400">
              {section.heading}
            </Headless.MenuHeading>
            {section.items.map((item) => (
              <Headless.MenuItem
                key={item.label}
                as="button"
                type="button"
                className="group flex w-full items-center rounded-md px-2.5 py-1.5 text-left text-[13px]/6 font-medium text-zinc-700 dark:text-zinc-200 data-focus:bg-blue-500 data-focus:text-white cursor-default outline-hidden"
                onClick={() => {
                  if (item.onClick) {
                    item.onClick();
                  } else if (item.path) {
                    navigate(item.path);
                  }
                }}
              >
                <span className="truncate">{item.label}</span>
                {item.badge && (
                  <span
                    className={`ml-auto shrink-0 rounded px-1 py-px text-[9px]/3 font-semibold ${badgeColors[item.badge.color] ?? 'bg-zinc-100 text-zinc-600'}`}
                  >
                    {item.badge.label}
                  </span>
                )}
              </Headless.MenuItem>
            ))}
          </Headless.MenuSection>
        ))}

        {/* Sign out */}
        <div className="mx-2 mt-1 mb-0.5 h-px bg-zinc-950/5 dark:bg-white/10" />
        <Headless.MenuItem
          as="button"
          type="button"
          className="group flex w-full items-center rounded-md px-2.5 py-1.5 mb-0.5 text-left text-[13px]/6 font-medium text-red-600 dark:text-red-400 data-focus:bg-red-500 data-focus:text-white cursor-default outline-hidden"
          onClick={handleLogout}
        >
          Sign out
        </Headless.MenuItem>
      </Headless.MenuItems>
    </Headless.Menu>
  );
};
